import React, { useState, useEffect } from "react";
import estoqueService from "../../services/estoqueService";
import fornecedoresService from "../../services/fornecedoresService";
import pedidosService from "../../services/pedidosService";
import comprasService, { STORAGE_OPEN_NOTA } from "../../services/comprasService";
import ItemEstoqueAutocomplete from "../../components/estoque/ItemEstoqueAutocomplete";

const fmtQtd = (v) => Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 3 });

export default function PedidoModal({
  show, onClose, onSaved, pedidoId, idCondominio, canEdit, saving, setSaving, onNavigateCompras,
}) {
  const [pedido, setPedido] = useState(null);
  const [fornecedores, setFornecedores] = useState([]);
  const [itensEstoque, setItensEstoque] = useState([]);
  const [idFornecedor, setIdFornecedor] = useState("");
  const [observacao, setObservacao] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [novoItem, setNovoItem] = useState({ idItem: "", quantidadePedida: "" });
  const [csvText, setCsvText] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !idCondominio) return;
    fornecedoresService.listar({ idCondominio, situacao: "ativo", limit: 200 })
      .then((r) => setFornecedores(r.data || [])).catch(() => {});
    estoqueService.listarItens({ idCondominio, ativo: "1", limit: 500 })
      .then((r) => setItensEstoque(r.data || [])).catch(() => {});
  }, [show, idCondominio]);

  useEffect(() => {
    if (!show) return;
    setError(null);
    if (pedidoId) {
      pedidosService.buscarPorId(pedidoId).then((r) => {
        const p = r.data;
        setPedido(p);
        setIdFornecedor(p.idFornecedor || "");
        setObservacao(p.observacao || "");
        setLinhas((p.itens || []).map((it) => ({
          idItem: it.idItem || it.iditem,
          cdItem: it.cdItem || it.cditem,
          nmItem: it.nmItem || it.nmitem,
          unidadeMedida: it.unidadeMedida || it.unidademedida,
          quantidadePedida: String(it.quantidadePedida ?? it.quantidadepedida ?? ""),
          vlrUnitarioEstimado: it.vlrUnitarioEstimado ?? it.vlrunitarioestimado ?? "",
        })));
      }).catch((e) => setError(e.message));
    } else {
      setPedido(null);
      setIdFornecedor("");
      setObservacao("");
      setLinhas([]);
    }
  }, [show, pedidoId]);

  const readOnly = !canEdit || (pedido && pedido.status !== "Rascunho");

  const salvar = async () => {
    if (!linhas.length) { setError("Adicione ao menos um item."); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        idCondominio,
        idFornecedor: idFornecedor || null,
        observacao,
        itens: linhas.map((l) => ({
          idItem: l.idItem,
          quantidadePedida: parseFloat(l.quantidadePedida),
          vlrUnitarioEstimado: l.vlrUnitarioEstimado !== "" ? parseFloat(l.vlrUnitarioEstimado) : null,
        })),
      };
      if (pedidoId) await pedidosService.atualizar(pedidoId, payload);
      else await pedidosService.criar(payload);
      onSaved?.();
      onClose?.();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const addLinha = () => {
    const it = itensEstoque.find((i) => String(i.id) === String(novoItem.idItem));
    if (!it || !parseFloat(novoItem.quantidadePedida)) return;
    if (linhas.some((l) => String(l.idItem) === String(it.id))) {
      setError("Item já está no pedido.");
      return;
    }
    setLinhas((p) => [...p, {
      idItem: it.id,
      cdItem: it.cdItem,
      nmItem: it.nmItem,
      unidadeMedida: it.unidadeMedida,
      quantidadePedida: novoItem.quantidadePedida,
      vlrUnitarioEstimado: "",
    }]);
    setNovoItem({ idItem: "", quantidadePedida: "" });
    setError(null);
  };

  const importarCsv = async () => {
    if (!pedidoId) {
      setError("Salve o pedido antes de importar CSV, ou cole linhas ao criar manualmente.");
      return;
    }
    const linhasCsv = csvText.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
      const [a, b] = line.includes(";") ? line.split(";") : line.split(",");
      return { cdItem: (a || "").trim(), quantidade: (b || "").trim() };
    });
    setSaving(true);
    try {
      const r = await pedidosService.importarItens(pedidoId, { linhas: linhasCsv, substituir: false });
      const p = r.data;
      setLinhas((p.itens || []).map((it) => ({
        idItem: it.idItem || it.iditem,
        cdItem: it.cdItem || it.cditem,
        nmItem: it.nmItem || it.nmitem,
        unidadeMedida: it.unidadeMedida || it.unidademedida,
        quantidadePedida: String(it.quantidadePedida ?? it.quantidadepedida ?? ""),
        vlrUnitarioEstimado: "",
      })));
      if (r.resumo?.erros?.length) {
        setError(`Importado com ${r.resumo.erros.length} linha(s) ignorada(s).`);
      }
      setCsvText("");
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const gerarNf = async () => {
    if (!pedidoId) return;
    setSaving(true); setError(null);
    try {
      const r = await comprasService.criarDePedido({ idPedido: pedidoId });
      const idNota = r.data?.id;
      if (idNota) sessionStorage.setItem(STORAGE_OPEN_NOTA, String(idNota));
      onSaved?.();
      onClose?.();
      if (onNavigateCompras) onNavigateCompras();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const mudarStatus = async (status) => {
    setSaving(true);
    try {
      await pedidosService.alterarStatus(pedidoId, status);
      onSaved?.();
      onClose?.();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {pedido ? `Pedido ${pedido.nrPedido || pedido.nrpedido}` : "Novo pedido"}
              {pedido?.status && (
                <span className="badge bg-secondary ms-2" style={{ fontSize: 11 }}>{pedido.status}</span>
              )}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2 small">{error}</div>}
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label small">Fornecedor</label>
                <select className="form-select form-select-sm" disabled={readOnly}
                  value={idFornecedor} onChange={(e) => setIdFornecedor(e.target.value)}>
                  <option value="">— Opcional —</option>
                  {fornecedores.map((f) => (
                    <option key={f.id} value={f.id}>{f.nmReduzido || f.nmRazaoSocial}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label small">Observação</label>
                <input className="form-control form-control-sm" disabled={readOnly}
                  value={observacao} onChange={(e) => setObservacao(e.target.value)} />
              </div>
            </div>

            <div className="table-responsive mb-3">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Código</th><th>Item</th><th>Un.</th>
                    <th className="text-end">Qtd pedida</th>
                    {!readOnly && <th />}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l, idx) => (
                    <tr key={l.idItem}>
                      <td><code>{l.cdItem}</code></td>
                      <td>{l.nmItem}</td>
                      <td>{l.unidadeMedida}</td>
                      <td className="text-end">
                        {readOnly ? fmtQtd(l.quantidadePedida) : (
                          <input type="number" step="0.001" className="form-control form-control-sm text-end"
                            style={{ width: 100, marginLeft: "auto" }}
                            value={l.quantidadePedida}
                            onChange={(e) => {
                              const v = e.target.value;
                              setLinhas((p) => p.map((x, i) => i === idx ? { ...x, quantidadePedida: v } : x));
                            }} />
                        )}
                      </td>
                      {!readOnly && (
                        <td>
                          <button type="button" className="btn btn-sm btn-outline-danger"
                            onClick={() => setLinhas((p) => p.filter((_, i) => i !== idx))}>
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {!linhas.length && (
                    <tr><td colSpan={5} className="text-muted text-center">Nenhum item</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {!readOnly && (
              <>
                <div className="d-flex flex-wrap gap-2 align-items-end mb-3 p-2 rounded" style={{ background: "var(--bg-highlight)" }}>
                  <div style={{ minWidth: 260, flex: 1 }}>
                    <label className="form-label small mb-0">Adicionar item</label>
                    <ItemEstoqueAutocomplete
                      listId="pedido-item-estoque-list"
                      itens={itensEstoque}
                      value={novoItem.idItem}
                      placeholder="Digite código ou descrição…"
                      onChange={(idItem) => setNovoItem((p) => ({ ...p, idItem }))}
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <label className="form-label small mb-0">Qtd</label>
                    <input type="number" step="0.001" className="form-control form-control-sm"
                      value={novoItem.quantidadePedida}
                      onChange={(e) => setNovoItem((p) => ({ ...p, quantidadePedida: e.target.value }))} />
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addLinha}>Incluir</button>
                </div>
                {pedidoId && (
                  <div className="mb-2">
                    <label className="form-label small">Importar CSV (código;quantidade por linha)</label>
                    <textarea className="form-control form-control-sm mb-1" rows={3} value={csvText}
                      placeholder={"EST-0001;10\nEST-0002,5"}
                      onChange={(e) => setCsvText(e.target.value)} />
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={importarCsv}>
                      Importar no pedido
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer flex-wrap gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Fechar</button>
            {canEdit && pedido?.status === "Rascunho" && (
              <>
                <button type="button" className="btn btn-outline-success btn-sm" disabled={saving}
                  onClick={() => mudarStatus("Aprovado")}>Aprovar</button>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={salvar}>
                  {saving ? "Salvando…" : "Salvar"}
                </button>
              </>
            )}
            {canEdit && pedido?.status === "Aprovado" && (
              <button type="button" className="btn btn-primary btn-sm" disabled={saving}
                onClick={() => mudarStatus("Enviado")}>Marcar enviado</button>
            )}
            {canEdit && pedidoId && ["Aprovado", "Enviado", "ParcialmenteRecebido"].includes(pedido?.status) && (
              <button type="button" className="btn btn-outline-primary btn-sm" disabled={saving}
                onClick={gerarNf}>
                <i className="bi bi-receipt me-1" /> Gerar NF (Compras)
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
